import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Input,
  Badge,
  Collapse,
  Button,
  Form,
  FormGroup,
  Label,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { auditLogsFixture, KNOWN_ACTIONS } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('audit');

/**
 * Audit-Log · brand-scoped, read-only.
 * Spec: orbit-specs/done/audit-log.md
 *
 * Endpoint: GET /v1/admin/audit-log (Plural per Spec)
 * Filter: action / userId / actorUserId / since / until
 */
export function AuditLogPage() {
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [actorUserId, setActorUserId] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [expanded, setExpanded] = useState(null);

  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  const query = useQuery({
    queryKey: ['auditLogs', { action, userId, actorUserId, since, until }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return auditLogsFixture({ action, userId, actorUserId, since, until });
      }
      const params = new URLSearchParams();
      if (action) params.set('action', action);
      if (userId) params.set('userId', userId);
      if (actorUserId) params.set('actorUserId', actorUserId);
      if (since) params.set('since', since);
      if (until) params.set('until', until);
      const res = await fetch(`/v1/admin/audit-log?${params.toString()}`);
      dlog('/v1/admin/audit-log raw', res);
      // Toleriert: {logs:[...]}, {entries:[...]}, {items:[...]}, direkt [...]
      const list = res?.logs ?? res?.entries ?? res?.items ?? res?.data
        ?? (Array.isArray(res) ? res : []);
      return { logs: list, raw: res };
    },
    // Spec: 30s Polling OK (Forensik, kein Live-Monitor)
    refetchInterval: 30_000,
  });

  const logs = query.data?.logs ?? [];
  const hasFilters = action || userId || actorUserId || since || until;

  return (
    <div>
      <PageHeader
        title="Audit-Log"
        subtitle="Operator- und System-Aktionen · GET /v1/admin/audit-log · Auto-Refresh 30s"
      />

      <FilterBar
        onReset={
          hasFilters
            ? () => {
                setAction('');
                setUserId('');
                setActorUserId('');
                setSince('');
                setUntil('');
              }
            : undefined
        }
        extras={
          <Form className="d-flex flex-wrap gap-2 align-items-end mb-0">
            <FormGroup className="mb-0">
              <Label className="small">Action</Label>
              <Input
                type="select"
                bsSize="sm"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                style={{ minWidth: 220 }}
              >
                <option value="">Alle</option>
                {KNOWN_ACTIONS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small">Aktor-ID</Label>
              <Input
                bsSize="sm"
                placeholder="z. B. 4321"
                value={actorUserId}
                onChange={(e) => setActorUserId(e.target.value.replace(/\D/g, ''))}
                style={{ maxWidth: 110 }}
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small">Subjekt-ID</Label>
              <Input
                bsSize="sm"
                placeholder="z. B. 4711"
                value={userId}
                onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
                style={{ maxWidth: 110 }}
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small">Von</Label>
              <Input type="date" bsSize="sm" value={since} onChange={(e) => setSince(e.target.value)} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small">Bis</Label>
              <Input type="date" bsSize="sm" value={until} onChange={(e) => setUntil(e.target.value)} />
            </FormGroup>
          </Form>
        }
      />

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!logs.length}
        emptyMessage={hasFilters ? 'Keine Treffer für die aktuellen Filter.' : 'Noch keine Audit-Einträge.'}
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 140 }}>Wann</th>
            <th style={{ width: 220 }}>Aktor</th>
            <th>Action</th>
            <th style={{ width: 200 }}>Subjekt</th>
            <th style={{ width: 130 }}>IP</th>
            <th style={{ width: 80 }}>Meta</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((e) => (
            <Row key={e.id} entry={e} expanded={expanded === e.id} onToggle={() => setExpanded(expanded === e.id ? null : e.id)} />
          ))}
        </tbody>
      </DataTable>

      {query.data?.total != null && (
        <p className="text-muted small mt-2 mb-0">
          {logs.length} angezeigt · Total: {query.data.total}
        </p>
      )}
    </div>
  );
}

function Row({ entry, expanded, onToggle }) {
  const hasMeta = entry.meta && Object.keys(entry.meta).length > 0;
  const isSystem = entry.actorUserId == null;
  const isLoginFailed = entry.action === 'security.login_failed';

  return (
    <>
      <tr>
        <td>
          <RelativeTime iso={entry.createdAt} />
        </td>
        <td>
          {isSystem ? (
            <Badge color="secondary" pill>SYSTEM</Badge>
          ) : (
            <>
              <Badge color="primary" pill className="me-2">#{entry.actorUserId}</Badge>
              <span className="small">{entry.actorEmail ?? '—'}</span>
            </>
          )}
        </td>
        <td>
          <ActionPill action={entry.action} />
        </td>
        <td className="small">
          {entry.userId != null ? (
            <>
              <span className="text-muted">#{entry.userId}</span> {entry.userEmail ?? ''}
            </>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td>
          <code className="small">{entry.ip ?? '—'}</code>
        </td>
        <td>
          {hasMeta ? (
            <Button color="link" size="sm" className="p-0" onClick={onToggle}>
              {expanded ? '▼' : '▶'} JSON
            </Button>
          ) : (
            <span className="text-muted small">—</span>
          )}
        </td>
      </tr>
      {hasMeta && (
        <tr>
          <td colSpan={6} className="p-0 border-0">
            <Collapse isOpen={expanded}>
              <div className="p-3" style={{ background: 'var(--bs-tertiary-bg)' }}>
                <div className="small text-muted mb-2">
                  <strong>UA:</strong> <code style={{ fontSize: 11 }}>{entry.userAgent ?? '—'}</code>
                </div>
                <pre
                  className="mb-0"
                  style={{
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                  }}
                >
                  {isLoginFailed ? '⚠ ' : ''}
                  {JSON.stringify(entry.meta, null, 2)}
                </pre>
              </div>
            </Collapse>
          </td>
        </tr>
      )}
    </>
  );
}

function ActionPill({ action }) {
  const namespace = action.split('.')[0];
  const color = NAMESPACE_COLORS[namespace] ?? 'secondary';
  return (
    <Badge color={color} pill className="orbit-code-badge">
      {action}
    </Badge>
  );
}

const NAMESPACE_COLORS = {
  user: 'primary',
  subscription: 'success',
  order: 'success',
  api_key: 'info',
  webhook: 'info',
  app_setting: 'warning',
  policy: 'warning',
  security: 'danger',
};
